import { useEffect, useState } from "react";

/**
 * Who owns the quick-chat dock.
 *
 * A module-level store rather than a context: the launcher lives in the app
 * topbar and the dock is mounted in AppShell's overlay slot, so the two are
 * siblings with no useful common provider below AppShell — and the same
 * store lets the /messages page, the command palette or a notification
 * deep-link open a conversation without prop-drilling.
 *
 * State is deliberately tiny: is the dock open, and which channel is showing.
 * The channel LIST and the thread come from the same react-query keys the
 * full page uses, so both surfaces stay in sync for free.
 */

const state = { open: false, channelId: null, poppedOut: false };
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());

export const quickChat = {
  get: () => state,

  open(channelId = null) {
    state.open = true;
    if (channelId !== null) state.channelId = channelId;
    emit();
  },

  close() {
    state.open = false;
    emit();
  },

  toggle() {
    state.open ? quickChat.close() : quickChat.open();
    emit();
  },

  select(channelId) {
    state.channelId = channelId;
    emit();
  },

  /** Back to the channel list inside the dock. */
  clearSelection() {
    state.channelId = null;
    emit();
  },

  setPoppedOut(value) {
    state.poppedOut = Boolean(value);
    if (!value) state.open = true; // returning from the pop-out re-docks
    emit();
  },

  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

/** Subscribe a component to the dock state. */
export const useQuickChat = () => {
  const [, force] = useState(0);

  useEffect(() => quickChat.subscribe(() => force((n) => n + 1)), []);

  return {
    ...quickChat.get(),
    open: quickChat.open,
    close: quickChat.close,
    toggle: quickChat.toggle,
    select: quickChat.select,
    clearSelection: quickChat.clearSelection,
    setPoppedOut: quickChat.setPoppedOut,
    isOpen: quickChat.get().open,
  };
};
